import type {
  MinimumBetSchema,
  PrizeDropPrizeSchema,
  PublicCampaignSchema,
} from "@/types";

/**
 * The rendered Prize Drop view object the components consume.
 *
 * This is NOT a wire schema — it is *assembled* (see `./assemble`) from the three
 * generated streams: `campaigns` (structural), `prize_drop_prizes` (volatile drop
 * counts), and `campaign_progress` (opt-in `joined`, carried alongside, not here).
 * Its data fields are **composed from the generated schemas** (not re-typed) so a
 * backend schema change surfaces here; only the shaping the built components need
 * (a synthetic React-key `id`, a `label` wrapper) is added on top.
 */

/** One prize tier — the volatile `PrizeDropPrizeSchema` shaped for rendering. */
export interface PrizeDropPrize
  extends Pick<
    PrizeDropPrizeSchema,
    "quantity" | "left_to_win" | "games" | "is_catalog_item"
  > {
  /** Synthetic stable row id (content-derived), for React keys. */
  id: string;
  /** Rendered, per-language/currency label — the backend prize `title`. */
  label: PrizeDropPrizeSchema["title"];
  /** Total eligible-games count when `games` is a preview subset. */
  games_count?: number;
}

export interface PrizeDrop {
  campaign: PublicCampaignSchema;
  /** From `PublicCampaignSchema.minimum_bet` (`MinimumBetSchema`); `display` is
   *  optional because the assembled zero/absent fallback omits it. */
  minimum_bet: Pick<MinimumBetSchema, "value" | "currency"> &
    Partial<Pick<MinimumBetSchema, "display">>;
  max_prizes_per_player: number | null;
  prizes: PrizeDropPrize[];
  eligible_games: string[] | "ALL";
  /** Total eligible-games count (list may be a preview subset). */
  eligible_games_count?: number;
}

/** Sidebar "REMAINING PRIZE DROPS" — sum of remaining across tiers. */
export const totalRemaining = (pd: PrizeDrop): number =>
  pd.prizes.reduce((sum, p) => sum + p.left_to_win, 0);

/** Every prize dropped: nothing left to win, so nothing left to join for —
 *  the button says "All dropped" and every Opt In surface goes quiet
 *  (INT-06). */
export const allDropped = (pd: PrizeDrop): boolean =>
  pd.prizes.length > 0 && pd.prizes.every((p) => p.left_to_win <= 0);

/** The top prize to show: the rarest tier that can still drop (tiers are
 *  assembled rarest-first) — a sold-out tier is never advertised (IGB-PD-03).
 *  One answer for the button, the home card and every "Top prize". */
export const topDroppingPrize = (pd: PrizeDrop): PrizeDropPrize | undefined =>
  pd.prizes.find((p) => p.left_to_win > 0);

/**
 * The reel's content: tiers that can still drop. The window appends the won prize itself if its
 * tier just emptied, and a single remaining tier makes it skip the spin entirely.
 *
 * Shared by both reveal surfaces — the in-game launcher button and the in-widget reveal card —
 * so the reel can't differ between them. Player-independent, unlike the won prize itself, which
 * is unknown until the claim succeeds (#3922).
 */
export const reelPrizes = (pd: PrizeDrop): string[] =>
  pd.prizes.filter((p) => p.left_to_win > 0).map((p) => p.label);

/* There is deliberately no title→tier lookup here any more. The old `revealPayload` needed one to
 * find a won prize's games, and it had to match on the RAW server label — `stripFreespinGameName`
 * collapses "100 Free Spins on Book of Dead" and "… on Starburst" to the same string, so matching
 * a formatted title attaches the wrong games. Since #3922 the disclosure push carries `games`
 * itself, so that fragile join is gone rather than merely unused. Reinstate it only with a caller,
 * and keep the raw-label rule if you do. */
